// Rules for the brand comparison set.
//
// The comparison used to be hardcoded to four chips (XQS · ACE · Zyn · Velo). The customer
// happens to care about Zyn and Velo; the next manufacturer cares about Loop, Helwit or
// Nordic Spirit, and one with many brands (Skruf) would produce unreadable spaghetti if
// everything were selected at once — he was explicit that one-at-a-time beats
// all-at-once as a default. So: free choice over the whole market, own brands
// pre-selected, and a cap on how many COMPETITORS can be plotted together.
//
// Pure so the rules are unit-tested without a picker.

/** Competitors plottable at once. Own brands are always allowed on top of this. */
export const MAX_COMPETITORS = 3

const norm = (s: string): string => s.trim().toUpperCase()

export function isOwn(brand: string, ownBrands: readonly string[]): boolean {
  const n = norm(brand)
  return ownBrands.some((o) => norm(o) === n)
}

export function competitorsIn(selection: readonly string[], ownBrands: readonly string[]): string[] {
  return selection.filter((b) => !isOwn(b, ownBrands))
}

/** True when another competitor would exceed the cap. Own brands are never blocked. */
export function isAtCompetitorCap(
  selection: readonly string[],
  ownBrands: readonly string[],
  max = MAX_COMPETITORS,
): boolean {
  return competitorsIn(selection, ownBrands).length >= max
}

/**
 * Add or remove `brand`. Removing always works — the customer asked to be able to drop
 * brands. Adding a competitor beyond the cap is refused (selection returned unchanged) so
 * the chart cannot silently become unreadable.
 */
export function toggleBrand(
  selection: readonly string[],
  brand: string,
  ownBrands: readonly string[],
  max = MAX_COMPETITORS,
): string[] {
  const n = norm(brand)
  if (selection.some((b) => norm(b) === n)) {
    return selection.filter((b) => norm(b) !== n)
  }
  if (!isOwn(brand, ownBrands) && isAtCompetitorCap(selection, ownBrands, max)) {
    return [...selection]
  }
  return [...selection, brand]
}

/**
 * The default set: the manufacturer's own brands, nothing else. A manufacturer opens on
 * its own brands and adds competitors deliberately.
 */
export function defaultSelection(ownBrands: readonly string[]): string[] {
  return [...new Set(ownBrands.filter((b) => b.trim()))]
}

/**
 * Plot order: own brands first (in their configured order), then competitors in the order
 * picked — so the emphasised series are the first thing the legend lists.
 */
export function orderedSelection(
  selection: readonly string[],
  ownBrands: readonly string[],
): string[] {
  const own = selection.filter((b) => isOwn(b, ownBrands))
  const rest = selection.filter((b) => !isOwn(b, ownBrands))
  const rank = (b: string) => ownBrands.findIndex((o) => norm(o) === norm(b))
  return [...own.sort((a, b) => rank(a) - rank(b)), ...rest]
}

/** Case- and accent-folded text, so "o" finds "ö" and "gotebor" finds "Göteborgs". */
export const foldText = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

/** Case- and accent-insensitive substring match for every picker's search box (MP-OS-189). */
export function matchesQuery(label: string, query: string): boolean {
  const q = foldText(query)
  return !q || foldText(label).includes(q)
}
